import { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

export default function BarChart({
    data = [],
    xLabel = "",
    yLabel = "",
    barColor = "#3182ce",
    plotMargin = { top: 30, right: 20, bottom: 120, left: 70 },
    showTooltip = true,
    transitionDuration = 1000
  }) {
    const ref = useRef();
    const containerRef = useRef();
    const tooltipRef = useRef();

    const [plotSize, setPlotSize] = useState(400);
    const [tooltipData, setTooltipData] = useState(null);
  
    useEffect(() => {
      function handleResize() {
        if (containerRef.current) {
          const size = Math.max(320, Math.min(containerRef.current.offsetWidth, 600));
          setPlotSize(size);
        }
      }
  
      handleResize();
      window.addEventListener('resize', handleResize);
  
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setTimeout(handleResize, 0);
          }
        });
      }, { threshold: 0.1 });
  
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
  
      return () => {
        window.removeEventListener('resize', handleResize);
        observer.disconnect();
        
      };
    }, []);
  
    useEffect(() => {
      if (!data.length) return;
      // Check if the SVG element still exists
      if (!ref.current) return;
      // Hide tooltip when data changes
      if (tooltipRef.current) {
        tooltipRef.current.style.display = 'none';
      }
      
      const svg = d3.select(ref.current);
      // Don't remove bars and axes - let them transition
      svg.selectAll('.d3-content:not(.bars-group):not(.x-axis):not(.y-axis)').remove();
  
      const margin = plotMargin;
      const width = plotSize;
      const height = plotSize;
      const innerWidth = width - margin.left - margin.right;
      const innerHeight = height - margin.top - margin.bottom;
  
      const x = d3.scaleBand()
        .domain(data.map(d => d.label))
        .range([0, innerWidth])
        .padding(0.18);
  
      const y = d3.scaleLinear()
        .domain([0, d3.max(data, d => d.value) || 1])
        .nice()
        .range([innerHeight, 0]);
  
      let xAxis = svg.select('.x-axis');
      if (xAxis.empty()) {
        xAxis = svg.append('g').attr('class', 'x-axis d3-content')
          .attr('transform', `translate(${margin.left},${margin.top + innerHeight})`);
      } else {
        xAxis.attr('transform', `translate(${margin.left},${margin.top + innerHeight})`);
      }
      xAxis.transition().duration(transitionDuration).call(d3.axisBottom(x))
        .selectAll("text")
        .attr("transform", "rotate(-30)")
        .style("text-anchor", "end")
        .style("font-size", "14px");
  
      let yAxis = svg.select('.y-axis');
      if (yAxis.empty()) {
        yAxis = svg.append('g').attr('class', 'y-axis d3-content')
          .attr('transform', `translate(${margin.left},${margin.top})`);
      } else {
        yAxis.attr('transform', `translate(${margin.left},${margin.top})`);
      }
      yAxis.transition().duration(transitionDuration).call(d3.axisLeft(y));
  
      // Preserve existing bars group for smooth transitions
      let barsGroup = svg.select('.bars-group');
      if (barsGroup.empty()) {
        barsGroup = svg.append('g')
          .attr('class', 'bars-group d3-content')
          .attr('transform', `translate(${margin.left},${margin.top})`);
      } else {
        barsGroup.attr('transform', `translate(${margin.left},${margin.top})`);
      }
      
      const bars = barsGroup.selectAll('rect').data(data, d => d?.label);
  
      bars.join(
        enter => enter.append('rect')
          .attr('x', d => x(d?.label))
          .attr('y', innerHeight) // Start at the bottom
          .attr('width', x.bandwidth())
          .attr('height', 0) // Start with zero height
          .attr('fill', barColor)
          .call(rect => {
            if (showTooltip) {
                rect
                .on('mouseenter', function (event, d) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const x = event.clientX - containerRect.left;
                  const y = event.clientY - containerRect.top;
                  if (tooltipRef.current) {
                    tooltipRef.current.style.display = 'block';
                    tooltipRef.current.style.left = `${x + 12}px`;
                    tooltipRef.current.style.top = `${y + 12}px`;
                  }
                  setTooltipData({...d, x, y});
                })
                .on('mousemove', function (event) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const x = event.clientX - containerRect.left;
                  const y = event.clientY - containerRect.top;
                  if (tooltipRef.current) {
                    tooltipRef.current.style.left = `${x + 12}px`;
                    tooltipRef.current.style.top = `${y + 12}px`;
                  }
                })
                .on('mouseleave', function () {
                  if (tooltipRef.current) {
                    tooltipRef.current.style.display = 'none';
                  }
                  setTooltipData(null);
                });
            }
          })
          .transition().duration(transitionDuration)
          .attr('y', d => y(d?.value))
          .attr('height', d => innerHeight - y(d?.value)),
        update => update
          .call(rect => {
            if (showTooltip) {
                rect
                .on('mouseenter', function (event, d) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const x = event.clientX - containerRect.left;
                  const y = event.clientY - containerRect.top;
                  if (tooltipRef.current) {
                    tooltipRef.current.style.display = "block";
                    tooltipRef.current.style.left = `${x + 12}px`;
                    tooltipRef.current.style.top = `${y + 12}px`;
                  }
                  setTooltipData({...d, x, y});
                })
                .on('mousemove', function (event) {
                  const containerRect = containerRef.current.getBoundingClientRect();
                  const x = event.clientX - containerRect.left;
                  const y = event.clientY - containerRect.top;
                  if (tooltipRef.current) {
                    tooltipRef.current.style.display = "block";
                    tooltipRef.current.style.left = `${x + 12}px`;
                    tooltipRef.current.style.top = `${y + 12}px`;
                  }
                })
                .on('mouseleave', function () {
                  if (tooltipRef.current) {
                    tooltipRef.current.style.display = 'none';
                  }
                  setTooltipData(null);
                });
            }
          })
          .transition().duration(transitionDuration)
          .attr('x', d => x(d.label))
          .attr('width', x.bandwidth())
          .attr('y', d => y(d.value))
          .attr('height', d => innerHeight - y(d.value)),
        exit => exit.remove()
      );
  
      svg.selectAll('.x-label').data([0]).join('text')
        .attr('class', 'x-label d3-content')
        .attr('x', margin.left + innerWidth / 2)
        .attr('y', height - 10)
        .attr('text-anchor', 'middle')
        .attr('font-size', 16)
        .text(xLabel);
  
      svg.selectAll('.y-label').data([0]).join('text')
        .attr('class', 'y-label d3-content')
        .attr('x', 18)
        .attr('y', margin.top + innerHeight / 2)
        .attr('text-anchor', 'middle')
        .attr('font-size', 16)
        .attr('transform', `rotate(-90, 18, ${margin.top + innerHeight / 2})`)
        .text(yLabel);
    }, [data, plotSize, plotMargin, xLabel, yLabel, barColor, showTooltip, transitionDuration]);
  
    return (
        <div ref={containerRef} className="w-full min-w-[320px] relative">
        <svg ref={ref} width="100%" height={plotSize} viewBox={`0 0 ${plotSize} ${plotSize}`} preserveAspectRatio="none" />
        {showTooltip && tooltipData && (
          <div
            ref={tooltipRef}
            className="absolute z-50 bg-white border border-gray-300 rounded shadow-lg px-3 py-2 text-xs pointer-events-none"
            style={{
              left: tooltipData.x + 12,
              top: tooltipData.y + 12,
              minWidth: 120,
            }}
          >
            <div><b>{tooltipData?.label}</b></div>
            <div>Weight: {tooltipData?.value?.toFixed(3)}</div>
          </div>
        )}
      </div>
      
    );
  }
  